import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useT } from "../context/locale";
import { realtime } from "../lib/ws";
import { useLive } from "../lib/use-live";
import { useResultReveal, type Reveal } from "../lib/use-reveal";
import {countdown} from "../lib/format";
import { useDuration, useRelative, useDateTime } from "../lib/use-relative";
import { Badge, Button, NumberChip } from "./ui";
import type { Draw, GameRules } from "../types/api";

/**
 * The live round, as the dashboard's opening statement.
 *
 * This used to be a thin strip above every screen, which put a ticking clock in
 * the operator's eye all day for the sake of information they only act on here.
 * Given the room, it can show the thing that actually matters — where the round
 * is in its cycle — instead of compressing it into a line of text.
 */
export function LiveRound() {
  const dateTime = useDateTime();
  const t = useT();
  const relative = useRelative();
  const duration = useDuration();
  /*
   * The round, the results table and the run switch are subscriptions.
   *
   * They used to be fetched once on mount and refetched on `draw.settled`, which
   * left the card showing whatever was true when the operator opened the page —
   * a game paused from another console, or a result corrected in the Draws
   * screen, did not reach it. The server now sends a fresh copy whenever any of
   * them change, and again on the heartbeat.
   */
  const current = useLive<Draw>("current-draw", {});
  const recentDraws = useLive<{ items: Draw[] }>("recent-draws", { limit: 8 });
  const rules = useLive<GameRules>("rules", {});

  const [draw, setDraw] = useState<Draw | null>(null);
  const [seconds, setSeconds] = useState<number | null>(null);
  /**
   * The lockdown window's own countdown, kept beside the draw countdown.
   *
   * Null when the round has no window, which is what keeps the badge below off
   * the card entirely rather than showing a second clock reading the same thing.
   */
  const [betSeconds, setBetSeconds] = useState<number | null>(null);
  const [connected, setConnected] = useState(false);

  const recent = recentDraws.data?.items ?? [];
  const paused = rules.data?.isRunning === false;
  const { reveal, shown, waiting } = useResultReveal();

  /*
   * The round the subscription is handing us, adopted as the card's own state.
   *
   * Kept in state rather than read straight from the subscription because the
   * countdown runs locally between server frames — the socket sends a tick a
   * second, and the interval below fills the gaps so the display never looks
   * frozen if one is late.
   */
  useEffect(() => {
    const next = current.data;
    if (!next) return;
    const until = (iso: string) =>
      Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));
    setDraw(next);
    setSeconds(until(next.closesAt));
    setBetSeconds(next.lockdownSeconds > 0 ? until(next.betsCloseAt) : null);
  }, [current.data]);

  useEffect(() => {
    const unsubscribe = realtime.subscribe((event, data) => {
      if (event === "__open") setConnected(true);
      if (event === "__close") setConnected(false);
      if (event === "draw.tick") {
        setSeconds(data.secondsRemaining as number);
        setBetSeconds(
          (data.lockdownSeconds as number) > 0
            ? (data.secondsUntilLockdown as number)
            : null,
        );
        setDraw((current) =>
          current && current.drawNo === data.drawNo
            ? current
            : ({
                ...(current ?? {}),
                drawNo: data.drawNo,
                periodCode: data.periodCode,
              } as Draw),
        );
      }
      // The boundary crossing, rather than waiting for the next tick frame.
      if (event === "draw.lockdown") setBetSeconds(0);
      // Nothing to refetch on settle: the server pushes the new round and the
      // results table to the subscriptions above as part of the same change.
    });

    // Local tick between server frames so the display never looks frozen.
    const timer = window.setInterval(() => {
      const step = (current: number | null) =>
        current === null || current <= 0 ? current : current - 1;
      setSeconds(step);
      setBetSeconds(step);
    }, 1000);

    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, []);

  const total = draw
    ? Math.max(
        1,
        Math.round(
          (new Date(draw.closesAt).getTime() -
            new Date(draw.opensAt).getTime()) /
            1000,
        ),
      )
    : 300;
  const elapsed =
    seconds === null ? 0 : Math.min(1, Math.max(0, (total - seconds) / total));
  const urgent = seconds !== null && seconds <= 30;

  return (
    <section className="panel overflow-hidden">
      <div
        className="grid gap-6 p-6 lg:grid-cols-[auto_1fr]"
        style={{
          background:
            "radial-gradient(52rem 20rem at 0% 0%, var(--color-crimson), transparent 70%)",
        }}
      >
        {/* While a result is landing, the numbers take the card. The countdown
            is meaningless for those few seconds — the round it belonged to has
            just ended — and leaving it running underneath is what made the
            transition read as a glitch rather than as a round finishing. */}
        {reveal ? (
          <ResultReveal reveal={reveal} shown={shown} waiting={waiting} />
        ) : (
        <div className="flex items-center gap-6">
          <Dial progress={elapsed} urgent={urgent}>
            <span
              className="money text-[30px] leading-none"
              style={{ color: urgent ? "var(--danger)" : "var(--accent)" }}
            >
              {seconds === null ? "--:--" : countdown(seconds)}
            </span>
            <span className="eyebrow mt-1">{t("dashboard.remaining")}</span>
          </Dial>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="size-1.5 shrink-0 rounded-full"
                style={{
                  background: connected ? "var(--success)" : "var(--text-dim)",
                  boxShadow: connected ? "0 0 8px var(--success)" : "none",
                }}
                aria-hidden
              />
              <p className="eyebrow">
                {paused ? t("dashboard.gamePaused") : t("dashboard.liveRound")}
              </p>
            </div>

            <p className="display mt-1 text-[26px]">
              {draw ? t("common.roundNo", { drawNo: draw.drawNo }) : "—"}
            </p>
            <p className="text-dim mt-1 text-[12px]">
              {draw
                ? t("draws.roundDetail", {
                    positions: draw.positionCount,
                    closesAt: dateTime(draw.closesAt),
                  })
                : "—"}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {paused ? (
                <Badge tone="danger">{t("dashboard.roundsStopped")}</Badge>
              ) : (
                <Badge tone="success">
                  {t("dashboard.everyInterval", { value: duration(total) })}
                </Badge>
              )}
              {betSeconds !== null ? (
                <Badge tone={betSeconds === 0 ? "danger" : "neutral"}>
                  {betSeconds === 0
                    ? t("dashboard.bettingShut")
                    : t("dashboard.bettingShutsIn", {
                        value: countdown(betSeconds),
                      })}
                </Badge>
              ) : null}
              <Link to="/games">
                <Button size="sm" variant="ghost">
                  {t("dashboard.gameSettings")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
        )}

        {/* Recent results, newest first. */}
        <div
          className="min-w-0 lg:border-l lg:pl-6"
          style={{ borderColor: "var(--border)" }}
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="eyebrow">{t("dashboard.latestNumbers")}</p>
            <Link
              to="/draws"
              className="eyebrow transition-colors hover:text-[var(--accent)]"
            >
              {t("dashboard.allDraws")} →
            </Link>
          </div>

          {recent.length === 0 ? (
            <p className="text-dim text-[13px]">{t("dashboard.noSettled")}</p>
          ) : (
            <ul className="space-y-2">
              {recent.slice(0, 5).map((round) => (
                <li key={round.drawNo}>
                  <Link
                    to={`/bets?drawNo=${round.drawNo}`}
                    className="flex flex-wrap items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-[var(--surface-hover)]"
                  >
                    <span className="money text-dim w-16 shrink-0 text-[11px]">
                      #{round.drawNo}
                    </span>
                    <span className="flex flex-wrap gap-1.5">
                      {round.results.map((value, index) => (
                        <NumberChip key={index} value={value} size="sm" />
                      ))}
                    </span>
                    <span className="text-dim ml-auto text-[11px]">
                      {relative(round.settledAt)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

/** A ring that fills as the round runs down. Decorative, but it reads at a glance. */
/**
 * A round's numbers landing, one position at a time.
 *
 * Driven entirely by what the socket delivered: `shown` counts up on a timer in
 * `useResultReveal`, and each chip animates in as it is added. Nothing here
 * polls and nothing re-reads the round — by the time this renders the result is
 * already decided, and the only job left is to show it properly.
 */
function ResultReveal({
  reveal,
  shown,
  waiting,
}: {
  reveal: Reveal;
  shown: number;
  waiting: number;
}) {
  const t = useT();
  const complete = shown >= reveal.results.length;

  return (
    <div className="reveal-panel flex min-w-0 flex-col justify-center gap-3">
      <div className="flex items-center gap-2">
        <span
          className="size-1.5 shrink-0 rounded-full"
          style={{ background: "var(--accent)", boxShadow: "0 0 10px var(--accent)" }}
          aria-hidden
        />
        <p className="eyebrow">{t("dashboard.justDrawn")}</p>
      </div>

      <p className="display text-[26px] leading-none">
        {t("common.roundNo", { drawNo: reveal.drawNo })}
      </p>

      {/*
        Announced once, when the set is complete, rather than per number — a
        screen reader should be given the result, not a running commentary on a
        four-second animation.
      */}
      <div
        className="flex flex-wrap items-center gap-2"
        aria-live="polite"
        aria-label={
          complete
            ? t("dashboard.resultAnnounce", {
                period: reveal.drawNo,
                numbers: reveal.results.join(", "),
              })
            : undefined
        }
      >
        {reveal.results.slice(0, shown).map((value, index) => (
          <span
            key={index}
            className="reveal-chip inline-flex"
            // The stagger comes from the index: the position count is
            // configurable, so it cannot be a fixed set of CSS rules.
            style={{ animationDelay: `${index * 40}ms` }}
          >
            <span
              className="chip-number"
              style={{ width: "2.6rem", height: "2.6rem", fontSize: 17 }}
            >
              {value}
            </span>
          </span>
        ))}

        {/* The positions still to come, so the row fills rather than grows. */}
        {reveal.results.slice(shown).map((_, index) => (
          <span
            key={`pending-${index}`}
            className="inline-flex rounded-full"
            style={{
              width: "2.6rem",
              height: "2.6rem",
              border: "1.5px dashed var(--border)",
              opacity: 0.5,
            }}
            aria-hidden
          />
        ))}
      </div>

      {waiting > 0 ? (
        <p className="text-dim text-[11px]">
          {t("dashboard.revealQueued", { count: waiting })}
        </p>
      ) : null}
    </div>
  );
}

function Dial({
  progress,
  urgent,
  children,
}: {
  progress: number;
  urgent: boolean;
  children: React.ReactNode;
}) {
  const size = 148;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--surface-inset)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={urgent ? "var(--danger)" : "var(--accent)"}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress)}
          style={{ transition: "stroke-dashoffset 1s linear" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  );
}
